import { useState, useRef, useEffect } from "react";
import ImageLibraryPicker from "../../../components/feature/ImageLibraryPicker";
import { fetchLandingPageConfig, putLandingPageConfig } from "../../../api/landingPage";
import { getUserFacingMessage, reportError } from "../../../api/errors";
import {
  mergeLandingPage,
  type Benefit,
  type ContactInfo,
  type EventItem,
  type HeroStat,
  type LandingPageConfig,
  type LandingPageUi,
} from "../../../api/landingPageConfig";
import { useAdminData } from "../AdminDataContext";
import { uploadTenantImageFile } from "../../../api/tenantImageUpload";

const ICON_OPTIONS = [
  "ri-ticket-2-line", "ri-shield-star-line", "ri-group-line", "ri-gift-2-line",
  "ri-vip-crown-line", "ri-football-line", "ri-map-pin-line", "ri-calendar-check-line",
  "ri-star-line", "ri-medal-line", "ri-heart-line", "ri-fire-line",
];

type TabKey = "hero" | "benefits" | "events" | "contact" | "labels";

const UI_FORM_FIELDS: { key: keyof LandingPageUi; label: string; hint?: string }[] = [
  { key: "statusBarTime", label: "状态栏时间（预览装饰）" },
  { key: "tabBenefits", label: "Tab · 会员权益标题" },
  { key: "tabEvents", label: "Tab · 近期活动标题" },
  { key: "sectionGalleryTitle", label: "图廊主标题" },
  { key: "sectionGallerySubtitle", label: "图廊副标题" },
  { key: "sectionTestimonialsTitle", label: "球迷说栏目标题" },
  { key: "sectionContactTitle", label: "联系我们标题" },
  { key: "contactQrHint", label: "二维码区说明" },
  { key: "contactWechatLabel", label: "微信一行 · 左侧标签" },
  { key: "contactPhoneLabel", label: "电话一行 · 左侧标签" },
  { key: "contactHoursPrefix", label: "工作时间 · 前缀（后接待见 hours 字段）" },
  { key: "browseButton", label: "底部 · 先去逛逛按钮" },
  { key: "welcomeBackTitle", label: "会员欢迎动效 · 主标题" },
  { key: "welcomeBackSubtitle", label: "会员欢迎动效 · 副标题", hint: "可用 {orgName}，保存后欢迎页会替换为协会名称" },
  { key: "memberRoleBadge", label: "会员欢迎动效 · 角标文案" },
  { key: "guestRoleLabel", label: "顶栏 · 游客标签" },
  { key: "memberRoleLabel", label: "顶栏 · 会员标签" },
  { key: "eventHotLabel", label: "活动列表 · 热门标签" },
];

export default function LandingPageEditor() {
  const { tenantLegacyId } = useAdminData();
  const [config, setConfig] = useState<LandingPageConfig>(() => mergeLandingPage(null));
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<TabKey>("hero");
  const [saved, setSaved] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [showSharePanel, setShowSharePanel] = useState(false);
  const [shareSuccess, setShareSuccess] = useState("");
  const [showLibraryPicker, setShowLibraryPicker] = useState(false);
  const heroBgFileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    fetchLandingPageConfig(tenantLegacyId)
      .then((c) => {
        if (!cancelled) setConfig(c);
      })
      .catch((e) => {
        if (!cancelled) {
          setLoadError(getUserFacingMessage(e, "落地页加载失败，请刷新。"));
          setConfig(mergeLandingPage(null));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [tenantLegacyId]);

  const updateConfig = (key: keyof LandingPageConfig, value: unknown) => {
    setConfig((p) => ({ ...p, [key]: value }));
  };

  const updateUi = (key: keyof LandingPageUi, value: string) => {
    setConfig((p) => ({ ...p, ui: { ...p.ui, [key]: value } }));
  };

  const updateStat = (index: number, field: keyof HeroStat, value: string) => {
    const stats = [...config.stats];
    stats[index] = { ...stats[index], [field]: value };
    updateConfig("stats", stats);
  };

  const updateBenefit = (index: number, field: keyof Benefit, value: string) => {
    const benefits = [...config.benefits];
    benefits[index] = { ...benefits[index], [field]: value };
    updateConfig("benefits", benefits);
  };

  const addBenefit = () => {
    if (config.benefits.length >= 8) return;
    updateConfig("benefits", [...config.benefits, { icon: "ri-star-line", label: "新权益", desc: "权益描述" }]);
  };

  const removeBenefit = (index: number) => {
    updateConfig("benefits", config.benefits.filter((_, i) => i !== index));
  };

  const updateEvent = (index: number, field: keyof EventItem, value: string | boolean) => {
    const events = [...config.events];
    events[index] = { ...events[index], [field]: value };
    updateConfig("events", events);
  };

  const addEvent = () => {
    if (config.events.length >= 6) return;
    updateConfig("events", [...config.events, { title: "新活动", date: "05.01", spots: "0/50", hot: false }]);
  };

  const removeEvent = (index: number) => {
    updateConfig("events", config.events.filter((_, i) => i !== index));
  };

  const updateContact = (field: keyof ContactInfo, value: string) => {
    updateConfig("contact", { ...config.contact, [field]: value });
  };

  const handleSave = () => {
    void putLandingPageConfig(tenantLegacyId, config)
      .then((raw) => {
        setConfig(mergeLandingPage(raw));
        setSaved(true);
        setTimeout(() => setSaved(false), 2500);
      })
      .catch((e) => {
        reportError(e);
      });
  };

  const TABS: { key: TabKey; label: string; icon: string }[] = [
    { key: "hero", label: "顶部主视觉", icon: "ri-image-2-line" },
    { key: "benefits", label: "会员权益", icon: "ri-gift-2-line" },
    { key: "events", label: "近期活动", icon: "ri-calendar-event-line" },
    { key: "contact", label: "联系方式", icon: "ri-customer-service-2-line" },
    { key: "labels", label: "界面文案", icon: "ri-text" },
  ];

  if (loading) {
    return (
      <div className="text-sm text-gray-500 py-12 text-center">正在加载落地页配置…</div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="font-bold text-gray-900 text-lg">落地页内容管理</h3>
          <p className="text-xs text-gray-400 mt-0.5">编辑手机端落地页（欢迎页）的展示内容，数据保存至服务器</p>
          {loadError && (
            <p className="text-xs text-amber-600 mt-1">加载失败，已使用默认：{loadError}</p>
          )}
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setPreviewOpen(true)}
            className="flex items-center gap-1.5 px-4 py-2 bg-gray-100 text-gray-700 text-sm rounded-xl hover:bg-gray-200 cursor-pointer whitespace-nowrap transition-colors"
          >
            <i className="ri-eye-line"></i>预览
          </button>
          <button
            onClick={handleSave}
            className={`flex items-center gap-1.5 px-4 py-2 text-sm rounded-xl cursor-pointer whitespace-nowrap transition-all ${
              saved ? "bg-green-500 text-white" : "bg-red-600 text-white hover:bg-red-700"
            }`}
          >
            <i className={saved ? "ri-check-line" : "ri-save-line"}></i>
            {saved ? "已保存" : "保存配置"}
          </button>
        </div>
      </div>

      {/* Info Banner */}
      <div className="bg-amber-50 border border-amber-100 rounded-xl p-4 mb-5 flex gap-3">
        <i className="ri-smartphone-line text-amber-500 text-base flex-shrink-0 mt-0.5"></i>
        <div className="text-xs text-amber-700 space-y-1">
          <p className="font-medium">关于落地页</p>
          <p>• 落地页是用户首次打开App时看到的欢迎/入会页面，是协会的品牌门面</p>
          <p>• 未入会用户将看到此页面，会员登录后直接进入首页</p>
          <p>• 修改后点击「保存配置」，手机端下次加载时生效</p>
        </div>
      </div>

      {/* Tab navigation */}
      <div className="flex gap-1 bg-gray-100 rounded-xl p-1 mb-6">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-medium cursor-pointer transition-all whitespace-nowrap ${
              activeTab === tab.key ? "bg-white text-gray-900" : "text-gray-500 hover:text-gray-700"
            }`}
          >
            <i className={tab.icon}></i>
            {tab.label}
          </button>
        ))}
      </div>

      {/* ───── Hero Tab ───── */}
      {activeTab === "hero" && (
        <div className="space-y-5">
          <div className="bg-white rounded-2xl border border-gray-100 p-5 space-y-4">
            <h4 className="font-semibold text-gray-800 text-sm">基本信息</h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-medium text-gray-600 block mb-1.5">协会名称</label>
                <input
                  type="text"
                  value={config.orgName}
                  onChange={(e) => updateConfig("orgName", e.target.value)}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-red-400"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 block mb-1.5">协会 Logo URL</label>
                <input
                  type="text"
                  value={config.logoUrl}
                  onChange={(e) => updateConfig("logoUrl", e.target.value)}
                  placeholder="图片地址，选填；与落地页一并保存"
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-red-400"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 block mb-1.5">主标语</label>
                <input
                  type="text"
                  value={config.slogan}
                  onChange={(e) => updateConfig("slogan", e.target.value)}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-red-400"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 block mb-1.5">副标语</label>
                <input
                  type="text"
                  value={config.subSlogan}
                  onChange={(e) => updateConfig("subSlogan", e.target.value)}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-red-400"
                />
              </div>
              <div className="col-span-2">
                <label className="text-xs font-medium text-gray-600 block mb-1.5">主视觉角标</label>
                <input
                  type="text"
                  value={config.heroBadge}
                  onChange={(e) => updateConfig("heroBadge", e.target.value)}
                  placeholder="如：本地首席球迷组织"
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-red-400"
                />
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 block mb-1.5">简介描述</label>
              <textarea
                value={config.heroDesc}
                onChange={(e) => updateConfig("heroDesc", e.target.value)}
                rows={2}
                maxLength={80}
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-red-400 resize-none"
              />
              <p className="text-right text-xs text-gray-400 mt-1">{config.heroDesc.length}/80</p>
            </div>
          </div>

          {/* 入会引导语 & 按钮文案 */}
          <div className="bg-white rounded-2xl border border-gray-100 p-5 space-y-4">
            <div className="flex items-center gap-2 mb-1">
              <h4 className="font-semibold text-gray-800 text-sm">入会引导</h4>
              <span className="text-xs bg-amber-50 text-amber-600 px-2 py-0.5 rounded-full font-medium">自定义话术</span>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 block mb-1.5">
                入会引导语 <span className="text-gray-400 font-normal">（显示在加入按钮上方）</span>
              </label>
              <textarea
                value={config.joinGuide}
                onChange={(e) => updateConfig("joinGuide", e.target.value)}
                rows={3}
                maxLength={120}
                placeholder="如：成为正式会员，享受全方位的球迷服务与专属权益..."
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-red-400 resize-none"
              />
              <p className="text-right text-xs text-gray-400 mt-1">{config.joinGuide.length}/120</p>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 block mb-1.5">
                加入按钮文案 <span className="text-gray-400 font-normal">（CTA按钮上显示的文字）</span>
              </label>
              <div className="flex items-center gap-3">
                <input
                  type="text"
                  value={config.joinButtonText}
                  onChange={(e) => updateConfig("joinButtonText", e.target.value)}
                  maxLength={20}
                  placeholder="如：立即加入协会"
                  className="flex-1 border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-red-400"
                />
                <div className="flex-shrink-0 px-4 py-2 bg-red-600 text-white text-sm rounded-xl font-medium whitespace-nowrap">
                  {config.joinButtonText || "按钮预览"}
                </div>
              </div>
              <p className="text-xs text-gray-400 mt-1">建议控制在 10 个字以内，右侧为实时预览</p>
            </div>
          </div>

          {/* Background image section */}
          <div className="bg-white rounded-2xl border border-gray-100 p-5 space-y-4">
            <h4 className="font-semibold text-gray-800 text-sm">背景图片</h4>
            {config.heroImage && (
              <div className="rounded-xl overflow-hidden border border-gray-100" style={{ height: 120 }}>
                <img src={config.heroImage} alt="hero" className="w-full h-full object-cover object-top" />
              </div>
            )}
            {/* Three upload methods */}
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => heroBgFileRef.current?.click()}
                className="flex items-center gap-1.5 px-3 py-2 bg-green-50 text-green-700 text-xs rounded-xl border border-green-200 hover:bg-green-100 cursor-pointer transition-colors whitespace-nowrap"
              >
                <i className="ri-upload-line"></i> 本地上传
              </button>
              <input
                ref={heroBgFileRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  void (async () => {
                    try {
                      const url = await uploadTenantImageFile(file, tenantLegacyId);
                      updateConfig("heroImage", url);
                    } catch (err) {
                      reportError(err, "背景图上传失败");
                    }
                    if (heroBgFileRef.current) heroBgFileRef.current.value = "";
                  })();
                }}
              />
              <button
                type="button"
                onClick={() => setShowLibraryPicker(true)}
                className="flex items-center gap-1.5 px-3 py-2 bg-amber-50 text-amber-700 text-xs rounded-xl border border-amber-200 hover:bg-amber-100 cursor-pointer transition-colors whitespace-nowrap"
              >
                <i className="ri-image-2-line"></i> 从资源库选择
              </button>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 block mb-1.5">图片链接</label>
              <input
                type="url"
                value={config.heroImage.startsWith("data:") ? "" : config.heroImage}
                onChange={(e) => updateConfig("heroImage", e.target.value)}
                placeholder="输入背景图片URL..."
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-red-400"
              />
              {config.heroImage.startsWith("data:") && (
                <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                  <i className="ri-checkbox-circle-line"></i> 已使用本地上传图片
                </p>
              )}
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-gray-100 p-5 space-y-4">
            <h4 className="font-semibold text-gray-800 text-sm">数据统计（Hero区域底部）</h4>
            <div className="grid grid-cols-3 gap-3">
              {config.stats.map((stat, i) => (
                <div key={i} className="space-y-2">
                  <input
                    type="text"
                    value={stat.num}
                    onChange={(e) => updateStat(i, "num", e.target.value)}
                    placeholder="如：0（实际会员数以接口为准）"
                    className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm text-center font-bold focus:outline-none focus:border-red-400"
                  />
                  <input
                    type="text"
                    value={stat.label}
                    onChange={(e) => updateStat(i, "label", e.target.value)}
                    placeholder="如：注册会员"
                    className="w-full border border-gray-200 rounded-xl px-3 py-2 text-xs text-center text-gray-500 focus:outline-none focus:border-red-400"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {activeTab === "labels" && (
        <div className="space-y-4">
          <p className="text-xs text-gray-500">
            以下文案随落地页接口下发至欢迎页，修改后请点击顶部「保存配置」。旧版 JSON 未包含这些字段时，前端会使用内置默认字再被此处覆盖。
          </p>
          <div className="bg-white rounded-2xl border border-gray-100 p-5 space-y-4">
            {UI_FORM_FIELDS.map(({ key, label, hint }) => (
              <div key={key}>
                <label className="text-xs font-medium text-gray-600 block mb-1.5">{label}</label>
                <input
                  type="text"
                  value={config.ui[key]}
                  onChange={(e) => updateUi(key, e.target.value)}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-red-400"
                />
                {hint && <p className="text-[10px] text-gray-400 mt-1">{hint}</p>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ───── Benefits Tab ───── */}
      {activeTab === "benefits" && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-500">最多可设置 8 条权益（当前 {config.benefits.length} 条）</p>
            {config.benefits.length < 8 && (
              <button onClick={addBenefit} className="flex items-center gap-1.5 px-3 py-1.5 bg-red-600 text-white text-xs rounded-xl cursor-pointer hover:bg-red-700 whitespace-nowrap">
                <i className="ri-add-line"></i>新增权益
              </button>
            )}
          </div>
          {config.benefits.map((benefit, i) => (
            <div key={i} className="bg-white rounded-2xl border border-gray-100 p-4 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-gray-500">权益 #{i + 1}</span>
                {config.benefits.length > 1 && (
                  <button onClick={() => removeBenefit(i)} className="w-6 h-6 flex items-center justify-center text-gray-300 hover:text-red-500 cursor-pointer rounded-lg transition-colors">
                    <i className="ri-close-line text-sm"></i>
                  </button>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-gray-600 block mb-1.5">标题</label>
                  <input
                    type="text"
                    value={benefit.label}
                    onChange={(e) => updateBenefit(i, "label", e.target.value)}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-red-400"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600 block mb-1.5">描述</label>
                  <input
                    type="text"
                    value={benefit.desc}
                    onChange={(e) => updateBenefit(i, "desc", e.target.value)}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-red-400"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 block mb-1.5">图标</label>
                <div className="flex flex-wrap gap-1.5">
                  {ICON_OPTIONS.map((icon) => (
                    <button
                      key={icon}
                      type="button"
                      onClick={() => updateBenefit(i, "icon", icon)}
                      className={`w-9 h-9 flex items-center justify-center rounded-xl cursor-pointer transition-all ${
                        benefit.icon === icon ? "bg-red-600 text-white" : "bg-gray-100 text-gray-500 hover:bg-gray-200"
                      }`}
                    >
                      <i className={`${icon} text-sm`}></i>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ───── Events Tab ───── */}
      {activeTab === "events" && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-500">落地页展示的近期活动（最多 6 条）</p>
            {config.events.length < 6 && (
              <button onClick={addEvent} className="flex items-center gap-1.5 px-3 py-1.5 bg-red-600 text-white text-xs rounded-xl cursor-pointer hover:bg-red-700 whitespace-nowrap">
                <i className="ri-add-line"></i>新增活动
              </button>
            )}
          </div>
          {config.events.map((evt, i) => (
            <div key={i} className="bg-white rounded-2xl border border-gray-100 p-4 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-gray-500">活动 #{i + 1}</span>
                {config.events.length > 1 && (
                  <button onClick={() => removeEvent(i)} className="w-6 h-6 flex items-center justify-center text-gray-300 hover:text-red-500 cursor-pointer rounded-lg transition-colors">
                    <i className="ri-close-line text-sm"></i>
                  </button>
                )}
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2">
                  <label className="text-xs font-medium text-gray-600 block mb-1.5">活动名称</label>
                  <input
                    type="text"
                    value={evt.title}
                    onChange={(e) => updateEvent(i, "title", e.target.value)}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-red-400"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600 block mb-1.5">日期（MM.DD）</label>
                  <input
                    type="text"
                    value={evt.date}
                    onChange={(e) => updateEvent(i, "date", e.target.value)}
                    placeholder="如：04.15"
                    className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-red-400"
                  />
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <label className="text-xs font-medium text-gray-600 block mb-1.5">名额（已报/总数）</label>
                  <input
                    type="text"
                    value={evt.spots}
                    onChange={(e) => updateEvent(i, "spots", e.target.value)}
                    placeholder="如：20/50"
                    className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-red-400"
                  />
                </div>
                <div className="flex items-center gap-2 pt-4">
                  <label className="text-xs font-medium text-gray-600 whitespace-nowrap">热门活动</label>
                  <button
                    type="button"
                    onClick={() => updateEvent(i, "hot", !evt.hot)}
                    className={`relative rounded-full transition-colors cursor-pointer ${evt.hot ? "bg-red-500" : "bg-gray-300"}`}
                    style={{ width: 36, height: 20 }}
                  >
                    <span
                      className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-all ${evt.hot ? "left-[18px]" : "left-0.5"}`}
                      style={{ width: 16, height: 16 }}
                    />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ───── Contact Tab ───── */}
      {activeTab === "contact" && (
        <div className="bg-white rounded-2xl border border-gray-100 p-5 space-y-4">
          <h4 className="font-semibold text-gray-800 text-sm">联系方式配置</h4>
          {[
            { key: "wechat" as const, label: "微信公众号ID", placeholder: "如：jianfans2018", icon: "ri-wechat-fill" },
            { key: "phone" as const, label: "联系电话", placeholder: "如：0796-12345678", icon: "ri-phone-fill" },
            { key: "email" as const, label: "邮箱地址", placeholder: "如：contact@xxx.com", icon: "ri-mail-fill" },
            { key: "address" as const, label: "办公地址", placeholder: "完整地址", icon: "ri-map-pin-fill" },
            { key: "hours" as const, label: "工作时间", placeholder: "如：周一至周六 9:00-18:00", icon: "ri-time-fill" },
          ].map((field) => (
            <div key={field.key}>
              <label className="text-xs font-medium text-gray-600 block mb-1.5 flex items-center gap-1.5">
                <i className={`${field.icon} text-red-400`}></i>
                {field.label}
              </label>
              <input
                type="text"
                value={config.contact[field.key]}
                onChange={(e) => updateContact(field.key, e.target.value)}
                placeholder={field.placeholder}
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-red-400"
              />
            </div>
          ))}
        </div>
      )}

      {/* ───── Preview Modal ───── */}
      {previewOpen && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-8">
          <div className="bg-white rounded-2xl w-[420px] max-h-[85vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <h4 className="font-bold text-gray-800">落地页预览</h4>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowSharePanel((p) => !p)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-xl cursor-pointer transition-all whitespace-nowrap ${
                    showSharePanel ? "bg-green-500 text-white" : "bg-green-50 text-green-600 hover:bg-green-100"
                  }`}
                >
                  <i className="ri-wechat-fill text-sm"></i> 分享至微信
                </button>
                <button onClick={() => { setPreviewOpen(false); setShowSharePanel(false); }} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer rounded-lg hover:bg-gray-100">
                  <i className="ri-close-line text-lg"></i>
                </button>
              </div>
            </div>

            {/* Share Panel */}
            {showSharePanel && (
              <div className="border-b border-gray-100 bg-green-50/60 px-5 py-4">
                <p className="text-xs font-semibold text-green-700 mb-3 flex items-center gap-1.5">
                  <i className="ri-share-line"></i> 将落地页分享至微信用户
                </p>
                {shareSuccess ? (
                  <div className="flex items-center gap-2 text-sm text-green-700 bg-green-100 px-3 py-2 rounded-xl">
                    <i className="ri-checkbox-circle-fill text-green-500"></i>
                    {shareSuccess}
                  </div>
                ) : (
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { icon: "ri-wechat-fill", label: "发送给好友", color: "bg-green-500" },
                      { icon: "ri-image-line", label: "分享朋友圈", color: "bg-teal-500" },
                      { icon: "ri-links-line", label: "复制链接", color: "bg-gray-500" },
                    ].map((action) => (
                      <button
                        key={action.label}
                        onClick={() => {
                          setShareSuccess(action.label === "复制链接" ? "链接已复制到剪贴板！" : `已${action.label}，等待微信确认`);
                          setTimeout(() => setShareSuccess(""), 3000);
                        }}
                        className="flex flex-col items-center gap-2 py-3 bg-white rounded-xl border border-green-100 hover:border-green-300 cursor-pointer transition-all"
                      >
                        <div className={`w-10 h-10 flex items-center justify-center ${action.color} rounded-full`}>
                          <i className={`${action.icon} text-white text-lg`}></i>
                        </div>
                        <span className="text-xs text-gray-600">{action.label}</span>
                      </button>
                    ))}
                  </div>
                )}
                <p className="text-[10px] text-green-500 mt-2.5 flex items-center gap-1">
                  <i className="ri-information-line"></i>
                  分享链接将包含当前落地页内容，适合发送给潜在入会用户
                </p>
              </div>
            )}

            <div className="overflow-y-auto flex-1">
              {/* Hero preview */}
              <div className="relative" style={{ height: 240 }}>
                {config.heroImage?.trim() ? (
                  <img src={config.heroImage} alt="hero" className="w-full h-full object-cover object-top" />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-gray-800 via-red-950 to-gray-900" aria-hidden />
                )}
                <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/80" />
                <div className="absolute top-4 left-4 flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-white/10 border border-white/20 flex items-center justify-center overflow-hidden">
                    {config.logoUrl?.trim() ? (
                      <img src={config.logoUrl.trim()} alt="" className="w-full h-full object-contain" />
                    ) : (
                      <i className="ri-football-line text-white text-lg" aria-hidden />
                    )}
                  </div>
                  <span className="text-white text-sm font-bold">{config.orgName}</span>
                </div>
                <div className="absolute bottom-0 left-0 right-0 px-4 pb-4 text-center">
                  <p className="text-white/90 text-[10px] font-medium mb-1">{config.heroBadge}</p>
                  <p className="text-white text-3xl font-black">{config.slogan}</p>
                  <p className="text-amber-400 text-xl font-bold">{config.subSlogan}</p>
                  <p className="text-white/70 text-xs mt-1">{config.heroDesc}</p>
                  <div className="flex justify-center gap-6 mt-3">
                    {config.stats.map((s, i) => (
                      <div key={i} className="text-center">
                        <p className="text-amber-400 text-lg font-black">{s.num}</p>
                        <p className="text-white/60 text-[10px]">{s.label}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              {/* Benefits preview */}
              <div className="p-4">
                <p className="text-xs font-semibold text-gray-400 mb-3">{config.ui.tabBenefits}</p>
                <div className="grid grid-cols-2 gap-2">
                  {config.benefits.map((b, i) => (
                    <div key={i} className="rounded-xl p-3" style={{ background: i % 2 === 0 ? "#FEF2F2" : "#FFFBEB" }}>
                      <div className="w-8 h-8 rounded-lg flex items-center justify-center mb-2" style={{ background: i % 2 === 0 ? "#DC2626" : "#D97706" }}>
                        <i className={`${b.icon} text-white text-sm`}></i>
                      </div>
                      <p className="text-gray-900 text-xs font-bold mb-0.5">{b.label}</p>
                      <p className="text-gray-500 text-[10px]">{b.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
              {/* Events preview */}
              <div className="px-4 pb-4">
                <p className="text-xs font-semibold text-gray-400 mb-3">{config.ui.tabEvents}</p>
                <div className="space-y-2">
                  {config.events.map((e, i) => {
                    const [filled, total] = e.spots.split("/").map(Number);
                    const pct = total > 0 ? Math.round((filled / total) * 100) : 0;
                    return (
                      <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-gray-50">
                        <div className="flex-shrink-0 w-10 h-10 rounded-xl flex flex-col items-center justify-center" style={{ background: e.hot ? "#DC2626" : "#6B7280" }}>
                          <span className="text-white text-xs font-black leading-none">{e.date.split(".")[1]}</span>
                          <span className="text-white/70 text-[8px]">/{e.date.split(".")[0]}</span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1 mb-1">
                            {e.hot && <span className="text-[9px] text-red-600 bg-red-50 px-1 py-0.5 rounded-full font-medium">{config.ui.eventHotLabel}</span>}
                            <p className="text-gray-900 text-xs font-semibold truncate">{e.title}</p>
                          </div>
                          <div className="flex-1 h-1 bg-gray-200 rounded-full overflow-hidden">
                            <div className="h-full rounded-full" style={{ width: `${pct}%`, background: pct >= 80 ? "#DC2626" : "#10B981" }} />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              {/* Contact preview */}
              <div className="px-4 pb-6 border-t border-gray-100 pt-4">
                <p className="text-xs font-semibold text-gray-400 mb-2">{config.ui.sectionContactTitle}</p>
                <div className="space-y-1.5 text-xs text-gray-500">
                  <p><i className="ri-wechat-fill text-green-500 mr-1.5"></i>{config.contact.wechat}</p>
                  <p><i className="ri-phone-fill text-red-400 mr-1.5"></i>{config.contact.phone}</p>
                  <p><i className="ri-mail-fill text-amber-500 mr-1.5"></i>{config.contact.email}</p>
                  <p><i className="ri-map-pin-fill text-teal-500 mr-1.5"></i>{config.contact.address}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Library Picker */}
      {showLibraryPicker && (
        <ImageLibraryPicker
          tenantLegacyId={tenantLegacyId}
          defaultCategory="landing"
          title="选择落地页背景图"
          onSelect={(img) => updateConfig("heroImage", img.url)}
          onClose={() => setShowLibraryPicker(false)}
        />
      )}
    </div>
  );
}
